import 'shepherd.js/dist/css/shepherd.css';
import { FeatureFlagService } from './FeatureFlagService';
import { constants } from '@/const';
import { UserService } from './UserService';
import { createOEMSearchTour } from './Tours/OEMSearchTour';
import { createOEMDiagramsTour } from './Tours/OEMDiagramsTour';
import { createSubAssemblyDiagramTour } from './Tours/SubAssemblyDiagramTour';
import type { ShepherdTour, TrackableTour, TourDefinition } from './Tours/TourHelpers';

type TourTrackingContext = {
  userId: number;
  tourId: number | null;
};

type ValidTourTrackingContext = {
  userId: number;
  tourId: number;
};

type QueuedTour = {
  name: string;
  trackingContext?: TourTrackingContext;
  onFinished?: () => void;
};

const tourRegistry: Map<string, { definition: TourDefinition; featureFlag?: string }> = new Map();
const tourQueue: QueuedTour[] = [];

export class TourService {
  private static activeTourName: string | null = null;
  private static activeTour: ShepherdTour | null = null;

  public static registerTour(name: string, definition: TourDefinition, featureFlag?: string) {
    tourRegistry.set(name, { definition, featureFlag });
  }

  public static get tourActive(): boolean {
    return document.querySelectorAll('.shepherd-element').length > 0;
  }

  public static startTour(name: string, trackingContext?: TourTrackingContext, onFinished?: () => void) {
    const entry = tourRegistry.get(name);
    if (!entry) {
      return;
    }

    if (entry.featureFlag && !FeatureFlagService.getFeatureEnabled(entry.featureFlag)) {
      return;
    }

    if (this.activeTourName !== null) {
      if (this.activeTourName === name) {
        // A late caller of the already-running tour still needs to know when it ends.
        if (onFinished && this.activeTour) {
          this.activeTour.on('complete', onFinished);
          this.activeTour.on('cancel', onFinished);
        }
        return;
      }
      const alreadyPending = tourQueue.some((queued) => queued.name === name);
      if (!alreadyPending) {
        tourQueue.push({ name, trackingContext, onFinished });
      }
      return;
    }

    this.activeTourName = name;
    try {
      const tour = entry.definition();
      this.activeTour = tour;
      attachTourPersistenceHandlers(tour, trackingContext);
      if (onFinished) {
        tour.on('complete', onFinished);
        tour.on('cancel', onFinished);
      }

      const releaseAndStartNext = () => {
        // Defer so Shepherd finishes removing this tour's modal overlay before the next tour mounts.
        setTimeout(() => {
          this.activeTourName = null;
          this.activeTour = null;
          this.startNextQueuedTour();
        }, 0);
      };
      tour.on('complete', releaseAndStartNext);
      tour.on('cancel', releaseAndStartNext);

      tour.start();
    } catch (error) {
      this.activeTourName = null;
      this.activeTour = null;
      throw error;
    }
  }

  /** Starts a tour once its first step's target element is in the DOM, giving up after timeoutMs. */
  public static startTourWhenReady(name: string, trackingContext?: TourTrackingContext, timeoutMs = 15000) {
    const entry = tourRegistry.get(name);
    if (!entry) {
      return;
    }

    const selector = entry.definition().steps[0]?.options?.attachTo?.element;
    if (typeof selector !== 'string') {
      this.startTour(name, trackingContext);
      return;
    }

    const deadline = Date.now() + timeoutMs;
    const poll = () => {
      if (document.querySelector(selector)) {
        this.startTour(name, trackingContext);
      } else if (Date.now() < deadline) {
        setTimeout(poll, 250);
      }
    };
    poll();
  }

  private static startNextQueuedTour() {
    while (this.activeTourName === null) {
      const next = tourQueue.shift();
      if (!next) {
        return;
      }
      this.startTour(next.name, next.trackingContext, next.onFinished);
    }
  }

  public static startEligibleTours(tourNames: string[], trackingContext?: TourTrackingContext) {
    for (const name of tourNames) {
      this.startTour(name, trackingContext);
    }
  }
}

function isValidTourTrackingContext(context?: TourTrackingContext): context is ValidTourTrackingContext {
  return !!context
    && Number.isInteger(context.userId)
    && context.userId > 0
    && typeof context.tourId === 'number'
    && Number.isInteger(context.tourId)
    && context.tourId > 0;
}

function attachTourPersistenceHandlers(tour: ShepherdTour, context?: TourTrackingContext): void {
  if (!isValidTourTrackingContext(context)) {
    return;
  }

  let endDate: string | null = null;
  let hasPersisted = false;
  const persistTourRecord = () => {
    if (hasPersisted) {
      return;
    }

    hasPersisted = true;
    const startDate = ((tour as TrackableTour).__tourStartDate ?? null);
    UserService.UpsertUserTours(context.userId, context.tourId, startDate, endDate)
      .catch(() => {
        // Keep tour UX uninterrupted if persistence fails.
      });
  };

  tour.on('complete', () => {
    endDate = new Date().toISOString();
    persistTourRecord();
  });

  tour.on('cancel', () => {
    const startDate = ((tour as TrackableTour).__tourStartDate ?? null);
    if (startDate === null) {
      endDate = new Date().toISOString();
    }
    persistTourRecord();
  });
}

TourService.registerTour('OEM Search Tour', createOEMSearchTour, constants.FEATURE_NAMES.TOUR_OEM_SEARCH);
TourService.registerTour('OEM Diagrams Tour', createOEMDiagramsTour, constants.FEATURE_NAMES.TOUR_OEM_DIAGRAMS);
TourService.registerTour('Sub-assembly Diagram Tour', createSubAssemblyDiagramTour);
